import Cookies from "js-cookie";

export enum StorageKeys {
  AuthAccessToken = "__smeetz-pos--auth-at",
  AuthRefreshToken = "__smeetz-pos--auth-rt",
  AuthExpiresIn = "__smeetz-pos--auth-ei",
  Language = "__smeetz-pos--lang",
  AuthAccessTokenB2B = "__smeetz--auth-at",
  AuthRefreshTokenB2B = "__smeetz--auth-rt",
  AuthExpiresInB2B = "__smeetz--auth-ei",
  SmtzAuthToken = "__smeetz-pos--auth-gql",
}

export enum SessionStorageKeys {
  // Kept for the window's lifetime, so the banner stays dismissed through memory reloads
  MemoryBannerDismissed = "__smeetz-memory-banner-dismissed",
}

export enum LocalStorageKeys {
  PosConfig = "__smeetz-pos--config",
  CheckIn = "__smeetz-pos--check-in",
  Booking = "__smeetz-booking",
  CashFloat = "__smeetz-cash-float",
  Device = "__smeetz-device",
  Receipt = "__smeetz-receipt",
  PrinterConfig = "__smeetz-printer",
  Logs = "__logs",
  ReceiptPrinter = "__smeetz-receipt-printer",
  TicketPrinter = "__smeetz-ticket-printer",
  App = "__smeetz-app",
  Common = "__smeetz-common",
  Order = "__smeetz-order",
  ReflectedStockSessions = "__smeetz-reflected-stock-sessions",
  Log = "__smeetz-log",
  Fiskaly = "__smeetz-fiskaly",
  manualHardwareUuid = "manualHardCodedHardwareUuid",
  Customer = "__smeetz-customer",
  PreventRefresh = "__smeetz-prevent-refresh",
  LastMemoryReload = "__smeetz-last-memory-reload",
  ExternalVouchers = "__smeetz-external-vouchers",
  Offers = "__smeetz-offers",
  Compliance = "__smeetz-compliance",
  DataMigration = "__smeetz-data-migration",
  Orders = "__smeetz-orders",
  OrdersMeta = "__smeetz-orders-meta",
  GroupOffers = "__smeetz-group-offers",
  // Last resolved cashbook flags per group
  FeatureFlags = "__smeetz-pos--feature-flags",
  // Read by the start page (start-page/index.html) at the next boot
  ReleaseChannel = "__smeetz-release-channel",
  ReadyVersion = "__smeetz-ready-version",
}

type AnyProps = Record<string, unknown>;

class Storage {
  public getLocalStorageItem(prop: string): string | null {
    const item = localStorage.getItem(prop);
    if (!item || item === "undefined") return null;
    return item;
  }

  public setLocalStorageItem(prop: string, value: string): void {
    return localStorage.setItem(prop, value);
  }

  public removeLocalStorageItem(prop: string) {
    return localStorage.removeItem(prop);
  }

  public getItem(prop: string): string | undefined {
    return Cookies.get(prop);
  }

  public setItem(
    prop: string,
    value: string | Date | AnyProps,
    options?: Cookies.CookieAttributes | undefined
  ): string | undefined {
    if (prop === "__smeetz-pos--auth-gql") {
      // lets add an expiry date to this cookie, expiry date in our graphql backend is 72 hours, we will use 48 hours
      // in 48 hours the cookie will be automatically removed by the browser and POS will do a refresh
      // this way we will never have a user logged in into POS but having an expired gql token
      options = { expires: 2 };
    }
    return Cookies.set(prop, value, options);
  }

  // eslint-disable-next-line
  public getJSON(prop = ""): any {
    // const data: string = this.getItem(prop)!;
    // return JSON.parse(data);
    const data = this.getItem(prop);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  public remove(
    prop: string,
    options?: Cookies.CookieAttributes | undefined
  ): void {
    return Cookies.remove(prop, options);
  }

  public clearAll() {
    const cookies = Cookies.get();
    for (const name of Object.keys(cookies)) {
      Cookies.remove(name);
    }
  }

  public clearTokens(): void {
    this.remove(StorageKeys.AuthAccessToken);
    this.remove(StorageKeys.AuthRefreshToken);
    this.remove(StorageKeys.AuthExpiresIn);
    this.remove(StorageKeys.SmtzAuthToken);

    // clearing b2b tokens as well
    this.remove(StorageKeys.AuthAccessTokenB2B);
    this.remove(StorageKeys.AuthRefreshTokenB2B);
    this.remove(StorageKeys.AuthExpiresInB2B);
  }
}

export default new Storage();
